import React from "react";
import { Link } from "react-router-dom";
import { useLanguage, useTheme } from "../App";
import { Sun, Moon, Globe, ArrowLeft, Shield, Lock, Server, Eye, Users, CheckCircle, Mail } from "lucide-react";

const TrustSecurity = () => {
  const { language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
                alt="SmartBooker" 
                className="h-8 w-auto"
              />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => changeLanguage(language === "it" ? "en" : "it")}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                <Globe className="w-5 h-5" />
              </button>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === "it" ? "Torna alla home" : "Back to home"}
          </Link>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            {/* Hero Section */}
            <div className="text-center mb-12">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-serif font-medium mb-4">Trust & Security – SmartBooker</h1>
              <p className="text-xl text-accent font-medium">La fiducia è la base di SmartBooker</p>
            </div>

            <p className="text-lg">
              SmartBooker è progettato per garantire ai saloni e ai loro clienti una piattaforma sicura, 
              affidabile e conforme alle normative europee sulla protezione dei dati.
            </p>
            
            <p className="font-medium text-muted-foreground">
              La sicurezza dei dati e la continuità del servizio sono tra le nostre priorità principali.
            </p>

            {/* Sicurezza dei dati */}
            <div className="mt-12 p-6 rounded-2xl bg-card border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className="text-xl font-semibold m-0">Sicurezza dei dati</h2>
              </div>
              
              <p>Proteggiamo i dati dei saloni e dei loro clienti utilizzando standard di sicurezza moderni.</p>
              <p>Le principali misure adottate includono:</p>
              
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="font-medium mb-1 flex items-center gap-2">
                    <span className="text-lg">🔒</span> Connessioni criptate (HTTPS)
                  </p>
                  <p className="text-sm text-muted-foreground m-0">
                    Tutte le comunicazioni con la piattaforma sono protette tramite crittografia SSL/TLS.
                  </p>
                </div>
                
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="font-medium mb-1 flex items-center gap-2">
                    <span className="text-lg">🔒</span> Protezione degli accessi
                  </p>
                  <p className="text-sm text-muted-foreground m-0">
                    Gli account sono protetti tramite autenticazione sicura e gestione delle autorizzazioni.
                  </p>
                </div>
                
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="font-medium mb-1 flex items-center gap-2">
                    <span className="text-lg">🔒</span> Monitoraggio continuo dei sistemi
                  </p>
                  <p className="text-sm text-muted-foreground m-0">
                    I nostri sistemi vengono monitorati per prevenire accessi non autorizzati e attività sospette.
                  </p>
                </div>
                
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="font-medium mb-1 flex items-center gap-2">
                    <span className="text-lg">🔒</span> Backup regolari
                  </p>
                  <p className="text-sm text-muted-foreground m-0">
                    I dati vengono salvati periodicamente per garantire continuità del servizio.
                  </p>
                </div>
              </div>
            </div>

            {/* Protezione dei dati personali */}
            <div className="mt-8 p-6 rounded-2xl bg-card border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-purple-500" />
                </div>
                <h2 className="text-xl font-semibold m-0">Protezione dei dati personali</h2>
              </div>
              
              <p>
                SmartBooker tratta i dati personali nel rispetto del{" "}
                <strong>Regolamento generale sulla protezione dei dati (GDPR)</strong>.
              </p>
              
              <p>Nel modello di trattamento:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>il <strong>salone è titolare</strong> dei dati dei propri clienti</li>
                <li><strong>SmartBooker è responsabile</strong> del trattamento, fornendo la piattaforma tecnologica.</li>
              </ul>
              
              <p>I dati vengono utilizzati esclusivamente per:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>gestione prenotazioni</li>
                <li>notifiche appuntamenti</li>
                <li>funzionamento della piattaforma</li>
              </ul>
              
              <div className="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                <p className="m-0 text-emerald-900 dark:text-emerald-100 font-medium">
                  SmartBooker non vende né utilizza i dati dei clienti per marketing di terze parti.
                </p>
              </div>
            </div>

            {/* Affidabilità della piattaforma */}
            <div className="mt-8 p-6 rounded-2xl bg-card border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                  <Server className="w-5 h-5 text-orange-500" />
                </div>
                <h2 className="text-xl font-semibold m-0">Affidabilità della piattaforma</h2>
              </div>
              
              <p>SmartBooker è progettato per garantire stabilità e continuità operativa.</p>
              <p>La piattaforma offre:</p>
              
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <span className="text-lg">⚙️</span>
                  <span>Infrastruttura cloud moderna</span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <span className="text-lg">⚙️</span>
                  <span>Aggiornamenti continui</span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <span className="text-lg">⚙️</span>
                  <span>Sistemi di protezione contro downtime</span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <span className="text-lg">⚙️</span>
                  <span>Monitoraggio delle prestazioni</span>
                </div>
              </div>
              
              <p className="mt-4 text-muted-foreground">
                Il nostro obiettivo è fornire ai saloni uno strumento affidabile per la gestione delle prenotazioni.
              </p>
            </div>

            {/* Trasparenza */}
            <div className="mt-8 p-6 rounded-2xl bg-card border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-cyan-500" />
                </div>
                <h2 className="text-xl font-semibold m-0">Trasparenza</h2>
              </div>
              
              <p>Crediamo nella massima trasparenza.</p>
              <p>Per questo motivo SmartBooker rende disponibili i seguenti documenti:</p>
              
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <Link 
                  to="/privacy-policy" 
                  className="flex items-center gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20 hover:bg-accent/10 transition-colors no-underline"
                >
                  <Shield className="w-4 h-4 text-accent" />
                  <span className="text-foreground">Privacy Policy</span>
                </Link>
                <Link 
                  to="/cookie-policy" 
                  className="flex items-center gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20 hover:bg-accent/10 transition-colors no-underline"
                >
                  <Shield className="w-4 h-4 text-accent" />
                  <span className="text-foreground">Cookie Policy</span>
                </Link>
                <Link 
                  to="/terms-conditions" 
                  className="flex items-center gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20 hover:bg-accent/10 transition-colors no-underline"
                >
                  <Shield className="w-4 h-4 text-accent" />
                  <span className="text-foreground">Termini e Condizioni</span>
                </Link>
                <Link 
                  to="/dpa" 
                  className="flex items-center gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20 hover:bg-accent/10 transition-colors no-underline"
                >
                  <Shield className="w-4 h-4 text-accent" />
                  <span className="text-foreground">Data Processing Agreement (DPA)</span>
                </Link>
              </div>
              
              <p className="mt-4 text-muted-foreground">
                Questi documenti spiegano nel dettaglio come vengono trattati e protetti i dati.
              </p>
            </div>

            {/* Responsabilità dei saloni */}
            <div className="mt-8 p-6 rounded-2xl bg-card border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-pink-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-pink-500" />
                </div>
                <h2 className="text-xl font-semibold m-0">Responsabilità dei saloni</h2>
              </div>
              
              <p>I saloni che utilizzano SmartBooker mantengono il <strong>controllo completo</strong> sui dati dei propri clienti.</p>
              <p>Questo significa che:</p>
              
              <ul className="list-disc pl-6 space-y-1">
                <li>il salone decide come utilizzare i dati</li>
                <li>il salone gestisce i rapporti con i propri clienti</li>
                <li>SmartBooker fornisce solo la tecnologia per gestire le prenotazioni</li>
              </ul>
            </div>

            {/* Il nostro impegno */}
            <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-accent/5 to-accent/10 border border-accent/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-xl font-semibold m-0">Il nostro impegno</h2>
              </div>
              
              <p>Il nostro obiettivo è fornire ai saloni uno strumento:</p>
              
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                  <span>✔</span> sicuro
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  <span>✔</span> conforme alle normative europee
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  <span>✔</span> affidabile nel tempo
                </div>
              </div>
              
              <p className="mt-4">
                SmartBooker è progettato per aiutare parrucchieri, barber shop e centri estetici a gestire 
                le prenotazioni in modo semplice e professionale.
              </p>
            </div>

            {/* Contatti */}
            <div className="mt-8 p-6 rounded-2xl bg-card border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gray-500/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-gray-500" />
                </div>
                <h2 className="text-xl font-semibold m-0">Contatti</h2>
              </div>
              
              <p>Per qualsiasi domanda relativa a sicurezza e protezione dei dati è possibile contattarci:</p>
              <p className="mt-2">
                <a 
                  href="mailto:info@smartbooker.it" 
                  className="inline-flex items-center gap-2 text-accent hover:underline font-medium"
                >
                  <Mail className="w-4 h-4" />
                  info@smartbooker.it
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © 2026 SmartBooker - Powered by AleNic Service and Consulting
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TrustSecurity;
